// src/App.jsx – full working version with Layout sidebar wrapper
// ---------------------------------------------------------------------
//  * Shows SplashScreen on first launch (4 s)
//  * Handles auth token + user fetch
//  * Wraps all "inside‑app" routes in <Layout /> so they inherit the
//    dark slate sidebar / logo theme.
//  * Login / Signup / AddressForm remain outside the wrapper.
//  * Keeps your existing fetch logic untouched – only routing changed.
// ---------------------------------------------------------------------

import { Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef } from "react";
import { Head } from "@unhead/react";
import api, {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  refreshAccessToken,
  logoutSession,
} from "./api";
import { App as CapApp } from "@capacitor/app";
import { Purchases } from "@revenuecat/purchases-capacitor";
import {
  Capacitor,
  SystemBars,
  SystemBarsStyle,
} from "@capacitor/core";
import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { setUserId as setWebAnalyticsUserId } from "firebase/analytics";
import { analytics } from "./firebase";
import useNativePush from "./hooks/useNativePush";
import { initNativeSocialLogin } from "./nativeSocialAuth";
import { Badge } from "@capawesome/capacitor-badge";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import {
  afSetUserId,
  afTrack,
  afGetMediaSource,
  afCaptureWebAttribution,
} from "./analytics/appsflyer";

// components / pages
import SplashScreen  from "./components/SplashScreen";
import Layout        from "./components/Layout";
import Dashboard     from "./pages/Dashboard";
import BuyPlans      from "./pages/BuyPlans";
import Trades        from "./pages/Trades";
import Withdrawals   from "./pages/Withdrawals";
import Settings      from "./pages/Settings";
import Resources     from "./pages/Resources";
import Login         from "./pages/Login";
import Signup        from "./pages/Signup";
import AddressForm   from "./components/AddressForm";
import Faq           from "./pages/Faq";
import Pricing       from "./pages/Pricing";
import Learn         from "./pages/Learn";
import Calculator    from "./pages/Calculator";
import KioskCalculator from "./pages/KioskCalculator";
import BoothDisplay from "./pages/BoothDisplay";
import MarketingLanding from "./pages/MarketingLanding";
import PaidTrafficLanding from "./pages/PaidTrafficLanding";
import Upgrade from "./pages/Upgrade";
import Review from "./pages/Review";
import SharePage from "./pages/SharePage";
import { initIAP }          from "./iap"; 
import ChangePassword from "./pages/ChangePassword";
import SelfCustodyArticle from "./pages/articles/SelfCustody";
import DcaVsLumpSum from "./pages/articles/DcaVsLumpSum";
import VerifyEmail from "./pages/VerifyEmail";
import ConfirmPlanChange from "./pages/ConfirmPlanChange";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword  from "./pages/ResetPassword";
import Terms from "./pages/Terms";
import PartnerClaim from "./pages/PartnerClaim";
import PartnerDashboard from "./pages/PartnerDashboard";
import PartnerStart from "./pages/PartnerStart";
import Privacy from "./pages/Privacy";
import DeleteAccountInfo from "./pages/DeleteAccountInfo";
import Blog from "./pages/Blog";
import BestWayToBuyBitcoin from "./pages/blog/BestWayToBuyBitcoin";
import BestBitcoinApp from "./pages/blog/BestBitcoinApp.jsx";
import SmartTimingVsSupercharged from "./pages/blog/SmartTimingVsSupercharged";
import SmartDcaVsDcaCaseStudy from "./pages/blog/SmartDcaVsDcaCaseStudy";
import BestBitcoinApps2026 from "./pages/blog/BestBitcoinApps2026";
import Download from "./pages/Download";
import {
  trackPageView,
  trackScreenView,
} from "./analytics/track";
import SatStackerShowDashboard from "./pages/SatStackerShowDashboard";
import BitcoinDca from "./pages/BitcoinDca";
import SmartDca from "./pages/SmartDca";
import SmartTimingResults from "./pages/SmartTimingResults";
import StrikeBitcoinDca from "./pages/StrikeBitcoinDca.jsx";
import Product from "./pages/Product";

/* ------------------------------------------------------------------ */
/*  Email-deep-link redirect wrappers                                  */
/* ------------------------------------------------------------------ */
function PartnerClaimEmailRedirect() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  return <Navigate to={`/partner-claim?token=${encodeURIComponent(token)}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [pathname]);

  return null;
}

/* ------------------------------------------------------------------ */
/*  Web 404 — keep bad URLs from silently becoming the homepage        */
/* ------------------------------------------------------------------ */
function NotFoundPage() {
  return (
    <>
      <Head>
        <title>Page Not Found | SatStacker</title>
        <meta name="robots" content="noindex,follow" />
      </Head>

      <main
        style={{
          minHeight: "100vh",
          background: "#0a0a0a",
          color: "#f0ede8",
          display: "grid",
          placeItems: "center",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 560 }}>
          <p
            style={{
              color: "#F7931A",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              marginBottom: 12,
            }}
          >
            404
          </p>

          <h1
            style={{
              fontFamily: "Georgia, serif",
              fontSize: "2rem",
              marginBottom: 12,
            }}
          >
            Page not found
          </h1>

          <p
            style={{
              color: "rgba(240,237,232,0.58)",
              lineHeight: 1.7,
              marginBottom: 24,
            }}
          >
            The page you requested does not exist or may have moved.
          </p>

          <a
            href="/"
            style={{
              display: "inline-block",
              background: "#F7931A",
              color: "#0a0a0a",
              padding: "12px 20px",
              borderRadius: 8,
              textDecoration: "none",
              fontWeight: 700,
            }}
          >
            Go to SatStacker
          </a>
        </div>
      </main>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  AppContent – everything that needs useNavigate                     */
/* ------------------------------------------------------------------ */
function AppContent() {
  
  const navigate = useNavigate();
  const location = useLocation();

  const SPLASH_KEY = "splashSeen_v1";
  const [showSplash, setShowSplash] = useState(() => {
    return sessionStorage.getItem(SPLASH_KEY) === "1" ? false : true;
  });
  const [token, setToken] = useState(
    () => getAccessToken() || ""
  );

  const [authReady, setAuthReady] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  useNativePush(token);
  const [buyPlans, setBuyPlans] = useState([]);
  const [trades, setTrades] = useState([]);
  const lastUrlRef = useRef("");
  const markHandled = (url) => { lastUrlRef.current = url; };
  const recentlyHandled = (url) => lastUrlRef.current === url;
  const rcLoggedFor = useRef(null);
  const [rcReady, setRcReady] = useState(!!window.__RC_READY__);
  const allowedPrefixes = [
    "/__/auth/handler",
    "/__/auth",
    "/confirm-plan-change",
    "/confirm-plan-delete",
    "/verify-email",
    "/reset-password",
    "/partner-claim",
    "/partner",
    "/partner-start",
    "/privacy",
    "/terms",
    "/trades",
    "/download",
    "/upgrade",
    "/review",
    "/share",
    "/pricing",
    "/faq",
    "/product",
    "/e",
    "/calculator",
    "/blog",
    "/articles",
    "/satstacker",
    "/bitcoin-dca",
    "/smart-dca", 
    "/start",
    "/smart-timing-results",
    "/strike-bitcoin-dca",
    "/e/product",
  ];
  const normalizePath = (p) => {
    if (!p) return "/";
    const q = p.toLowerCase();
    return q.endsWith("/") && q !== "/" ? q.slice(0, -1) : q;
  };
  const isAllowed = (p) => {
    const n = normalizePath(p);
    return allowedPrefixes.some(pref => n.startsWith(pref));
  };
  const toAppRoute = (path, search = "") => {
    const p = normalizePath(path);
    return `${p}${search || ""}`;   // ✅ same for native + web
  };
  useEffect(() => {
    if (!userInfo?.id) return;

    console.log("[AUTH] setting AppsFlyer CUID for", userInfo.id);

    // pass raw id, let appsflyer.js format it to user_${id}
    afSetUserId(userInfo.id, { debug: false });

    afTrack("user_identified", { user_id: `user_${userInfo.id}` }, { debug: false });
  }, [userInfo?.id]);
  useEffect(() => {
    if (!token || !userInfo?.id) return;
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    let timeoutId = null;
    let attempts = 0;

    const placeholderSources = new Set([
      null,
      "",
      "organic",
      "unknown",
      "direct",
      "firebase",
      "google.com",
      "apple.com",
    ]);

    const syncAcquisitionSource = async () => {
      attempts += 1;

      try {
        const currentSource =
          userInfo.acquisition_source ?? null;

        if (!placeholderSources.has(currentSource)) {
          return;
        }

        const source = await afGetMediaSource();

        if (source && source !== "organic") {
          await api.patch(
            "/me",
            {
              acquisition_source: source,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!cancelled) {
            await fetchUserInfo(token);
          }

          return;
        }
      } catch (error) {
        console.warn(
          "[Attribution] sync failed",
          error
        );
      }

      if (!cancelled && attempts < 20) {
        timeoutId = window.setTimeout(
          syncAcquisitionSource,
          3000
        );
      }
    };

    syncAcquisitionSource();

    return () => {
      cancelled = true;

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    token,
    userInfo?.id,
    userInfo?.acquisition_source,
  ]);
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let appStateListener;
    let cancelled = false;

    const clearBadge = async () => {
      try {
        await Badge.clear();
      } catch (error) {
        console.warn("[Badge] clear failed", error);
      }

      try {
        await FirebaseMessaging.removeAllDeliveredNotifications();
      } catch (error) {
        console.warn(
          "[Push] failed to clear delivered notifications",
          error
        );
      }
    };

    const setUp = async () => {
      await clearBadge();

      const listener = await CapApp.addListener(
        "appStateChange",
        ({ isActive }) => {
          if (isActive) {
            clearBadge();
          }
        }
      );

      if (cancelled) {
        await listener.remove();
        return;
      }

      appStateListener = listener;
    };

    setUp();

    return () => {
      cancelled = true;
      appStateListener?.remove();
    };
  }, []);
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    (async () => {
      try {
        await SystemBars.setStyle({
          style: SystemBarsStyle.Dark,
        });
      } catch (error) {
        console.warn("[SystemBars] setup failed", error);
      }
    })();
  }, []);
  useEffect(() => {
    const h = () => setRcReady(true);
    document.addEventListener("rc-ready", h, { once: true });
    if (window.__RC_READY__) setRcReady(true);
    return () => document.removeEventListener("rc-ready", h);
  }, []);

  const iapInitOnce = useRef(false);
    useEffect(() => {
      if (!Capacitor.isNativePlatform()) return;
      if (!rcReady || !token || !userInfo?.id) return;

      const uid = String(userInfo.id);

      if (
        window.__RC_LOGGED__ === uid ||
        rcLoggedFor.current === uid
      ) {
        return;
      }

      // Reserve this ID immediately to prevent overlapping logIn calls.
      rcLoggedFor.current = uid;

      let cancelled = false;

      const identifyRevenueCatUser = async () => {
        try {
          const result = await Purchases.logIn({
            appUserID: uid,
          });

          if (cancelled) return;

          window.__RC_LOGGED__ = uid;
          window.__USER_ID__ = uid;
          rcLoggedFor.current = uid;

          if (!iapInitOnce.current) {
            iapInitOnce.current = true;

            try {
              await initIAP();
            } catch (error) {
              iapInitOnce.current = false;
              console.error("[IAP] initialization failed", error);
            }
          }

          await Purchases.invalidateCustomerInfoCache();

          try {
            await api.post("/api/subscriptions/revenuecat/sync");
          } catch (error) {
            console.warn(
              "[RC] backend subscription sync failed",
              error
            );
          }

          console.log("[RC] logged in as", uid);

          if (result?.customerInfo) {
            console.log(
              "[RC] active entitlements",
              Object.keys(result.customerInfo.entitlements?.active || {})
            );
          }
        } catch (error) {
          if (rcLoggedFor.current === uid) {
            rcLoggedFor.current = null;
          }

          console.warn("[RC] logIn failed", error);
        }
      };

      identifyRevenueCatUser();

      return () => {
        cancelled = true;
      };
    }, [token, userInfo?.id, rcReady]);

  // After successful login, honor ?redirect= first; then (optionally) auto-run Strike connect
  useEffect(() => {
    if (!token || !userInfo) return;

    // 1) respect an explicit redirect param (e.g., /login?redirect=/settings)
    const sp = new URLSearchParams(location.search);
    const redirect = sp.get("redirect");
    if (redirect) {
      // clear the query and go
      navigate(redirect, { replace: true });
      return;
    }

    // 2) only if user explicitly tried to connect Strike before logging in
    const action = localStorage.getItem("postLoginAction");
    if (action === "connectStrike") {
      localStorage.removeItem("postLoginAction");
      // Send to settings with a one-shot flag; Settings will call handleConnectStrike() once
      navigate("/settings?do=strike-connect", { replace: true });
    }
  }, [token, userInfo, location.search, navigate]);

  const fetchTrades = async () => {
    try {
      const { data } = await api.get("/trades");
      setTrades(data);
    } catch (err) {
      console.error("Error fetching trades", err);
    }
  };

  // Fetch after user is ready
  useEffect(() => {
    if (token && userInfo) fetchTrades();
  }, [token, userInfo]);

  const SPLASH_DURATION_MS = 3400;

  useEffect(() => {
    if (!showSplash) return;
    const timer = setTimeout(() => {
      setShowSplash(false);
      sessionStorage.setItem(SPLASH_KEY, "1");
    }, SPLASH_DURATION_MS);
    return () => clearTimeout(timer);
  }, [showSplash]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrapSession() {
      try {
        let current = getAccessToken();

        if (!current) {
          current = await refreshAccessToken();
        }

        if (!cancelled && current) {
          setToken(current);
        }
      } catch {
        if (!cancelled) {
          clearAccessToken();
          setToken("");
        }
      } finally {
        if (!cancelled) {
          setAuthReady(true);
        }
      }
    }

    bootstrapSession();

    return () => {
      cancelled = true;
    };
  }, []);

  // fetch user info once token is present
  const [fetchAttempted, setFetchAttempted] = useState(false);

  useEffect(() => {
    const pagePath =
      location.pathname +
      location.search;

    // Preserve first-touch UTM / AppsFlyer-style query params on web.
    afCaptureWebAttribution(
      location.search
    );

    trackPageView({
      url: pagePath,
      title:
        typeof document !== "undefined"
          ? document.title
          : "",
      referrer:
        typeof document !== "undefined"
          ? document.referrer
          : "",
      location:
        typeof window !== "undefined"
          ? window.location.href
          : "",
    });

    if (Capacitor.isNativePlatform()) {
      trackScreenView({
        screenName: location.pathname,
        screenClass: location.pathname,
      });
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!userInfo?.id) return;

    const userId =
      String(userInfo.id);

    if (Capacitor.isNativePlatform()) {
      FirebaseAnalytics.setUserId({
        userId,
      }).catch((err) => {
        console.warn(
          "[FirebaseAnalytics] setUserId failed",
          err
        );
      });

      return;
    }

    if (analytics) {
      try {
        setWebAnalyticsUserId(
          analytics,
          userId
        );
      } catch (err) {
        console.warn(
          "[Analytics] web setUserId failed",
          err
        );
      }
    }
  }, [userInfo?.id]);

  // fetch buy plans after user info is available
  useEffect(() => {
    if (token && userInfo) fetchBuyPlans();
  }, [token, userInfo]);

  useEffect(() => {
    initNativeSocialLogin().catch((e) => console.warn("[SocialLogin init]", e));
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let appUrlListener;
    let cancelled = false;

    const setUp = async () => {
      const listener = await CapApp.addListener(
        "appUrlOpen",
        ({ url }) => {
          try {
            const u = new URL(url);

            const isOurLink =
              u.protocol === "satstacker:" ||
              u.hostname.endsWith("satstacker.app");

            if (!isOurLink) return;
            if (recentlyHandled(u.href)) return;
            if (!isAllowed(u.pathname)) return;

            navigate(
              toAppRoute(u.pathname, u.search),
              { replace: true }
            );

            markHandled(u.href);
          } catch (error) {
            console.warn(
              "Failed to parse appUrlOpen URL:",
              url,
              error
            );
          }
        }
      );

      if (cancelled) {
        await listener.remove();
        return;
      }

      appUrlListener = listener;
    };

    setUp();

    return () => {
      cancelled = true;
      appUrlListener?.remove();
    };
  }, [navigate]);

useEffect(() => {
  if (userInfo?.id != null) {
    window.__USER_ID__ = String(userInfo.id);
  }
}, [userInfo?.id])

useEffect(() => {
  if (!Capacitor.isNativePlatform()) return;

  let cancelled = false;

  const configureRevenueCat = async () => {
    try {
      if (!window.__RC_CONFIG_PROMISE__) {
        const platform = Capacitor.getPlatform();

        const apiKey =
          platform === "android"
            ? import.meta.env.VITE_RC_ANDROID_KEY
            : import.meta.env.VITE_RC_IOS_KEY;

        if (!apiKey) {
          throw new Error(
            `[RC] Missing RevenueCat key for ${platform}`
          );
        }

        window.__RC_CONFIG_PROMISE__ = Purchases.configure({
          apiKey,
        }).catch((error) => {
          // Allow a later retry if configuration genuinely fails.
          window.__RC_CONFIG_PROMISE__ = null;
          throw error;
        });
      }

      await window.__RC_CONFIG_PROMISE__;

      if (cancelled) return;

      window.__RC_READY__ = true;
      setRcReady(true);
      document.dispatchEvent(new Event("rc-ready"));

      console.log("[RC] configured");
    } catch (error) {
      console.warn("[RC] configure failed", error);
    }
  };

  configureRevenueCat();

  return () => {
    cancelled = true;
  };
}, []);

async function waitForRevenueCatReady(timeoutMs = 10000) {
  const start = Date.now();
  while (!window.__RC_READY__) {
    if (Date.now() - start > timeoutMs) throw new Error("Billing not ready (timeout).");
    await new Promise(r => setTimeout(r, 100));
  }
}

useEffect(() => {
  (async () => {
    try {
      const launch = await CapApp.getLaunchUrl();
      const url = launch?.url;
      if (!url) return;
      const u = new URL(url);
      const isOurLink = u.protocol === "satstacker:" || u.hostname.endsWith("satstacker.app");
      if (!isOurLink) return;

      if (recentlyHandled(u.href)) return;
      if (!isAllowed(u.pathname)) return;
      navigate(toAppRoute(u.pathname, u.search), { replace: true });
      markHandled(u.href);
    } catch (e) {
      console.warn("getLaunchUrl failed", e);
    }
  })();
}, [navigate]);

  const fetchUserInfo = async (tokenOverride) => {
    if (tokenOverride) {
      setAccessToken(tokenOverride);
    }

    const tok =
      tokenOverride ||
      getAccessToken();

    if (!tok) {
      return;
    }

    try {
      // api interceptor supplies Authorization.
      const { data } = await api.get("/me");

      setUserInfo(data);
    } catch (err) {
      console.error(
        "User info fetch failed",
        err
      );

      if (err?.response?.status === 401) {
        clearAccessToken();
        setToken("");
        setUserInfo(null);
      }
    }
  };

  const fetchBuyPlans = async () => {
    try {
      const { data } = await api.get(`/buyplans`, {
        params: { user_id: userInfo.id },
      });
      setBuyPlans(data);
    } catch (err) {
      console.error("Error fetching buy plans", err);
    }
  };

  // show splash (except for signup or address)
  const noSplashPrefixes = [
    "/blog",
    "/articles",
    "/pricing",
    "/faq",
    "/signup",
    "/login",
    "/address",
    "/confirm-plan-change",
    "/confirm-plan-delete",
    "/verify-email",
    "/reset-password",
    "/satstacker",
    "/privacy",
    "/terms",
    "/download",
    "/upgrade",
    "/review",
    "/share",
    "/bitcoin-dca",
    "/product",
    "/strike-bitcoin-dca",
    "/smart-timing-results",
    "/smart-dca",
    "/e/review",
    "/e/upgrade",
    "/e/share",
    "/e/trades",
    "/calculator",
    "/e/calculator",
    "/kiosk",
    "/e/kiosk",
    "/booth-display",
    "/e/booth-display",
    "/e/satstacker",
    "/e/bitcoin-dca",
    "/e/strike-bitcoin-dca",
    "/e/smart-dca",
    "/e/smart-timing-results",
    "/start",
  ];

  const shouldShowSplash =
    showSplash &&
    location.pathname !== "/" &&
    !noSplashPrefixes.some((p) => location.pathname.startsWith(p));

  // fetch user info once token is present, but prevent infinite retry
  useEffect(() => {
    if (!token || fetchAttempted || userInfo) return;

    fetchUserInfo(token);   // ✅ now it works
    setFetchAttempted(true);
  }, [token, userInfo, fetchAttempted]);

  if (shouldShowSplash) return <SplashScreen />;

  function SatStackerShowRedirect() {
    const oneLink = "https://satstacker.onelink.me/vXP4/satstacker";

    useEffect(() => {
      window.location.replace(oneLink);
    }, []);

    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        textAlign: "center",
        background: "#0E0F12",
        color: "#fff"
      }}>
        <div>
          <h1>Opening SatStacker…</h1>
          <p>If nothing happens, tap below.</p>
          <a
            href={oneLink}
            style={{
              display: "inline-block",
              marginTop: 16,
              padding: "12px 18px",
              borderRadius: 8,
              background: "#f97316",
              color: "#fff",
              textDecoration: "none",
              fontWeight: 700
            }}
          >
            Open SatStacker
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public confirm routes (outside Layout) */}
        <Route path="/confirm-plan-change" element={<ConfirmPlanChange />} />
        <Route path="/confirm-plan-delete" element={<ConfirmPlanChange />} />
        <Route path="/" element={
          Capacitor.isNativePlatform() || (token && userInfo)
            ? <Navigate to="/dashboard" replace />
            : <MarketingLanding />
        } />
        <Route
          path="/start"
          element={
            token && userInfo
              ? (
                <Navigate
                  to="/plans?smartTiming=1"
                  replace
                />
              )
              : (
                <PaidTrafficLanding />
              )
          }
        />
        <Route
          path="/creator/satstacker-show"
          element={
            token ? (
              <SatStackerShowDashboard />
            ) : (
              <Navigate to="/login?redirect=/creator/satstacker-show" replace />
            )
          }
        />

        {/* Booth display — NO layout, NO sidebar, full screen */}
        <Route path="/booth-display" element={<BoothDisplay />} />

        {/* Layout should NOT guard auth — it’s just the shell */}
        <Route element={<Layout userInfo={userInfo} /* ok if null */ />}>
          {/* PUBLIC routes keep the SAME layout styling */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/best-way-to-buy-bitcoin" element={<BestWayToBuyBitcoin />} />
          <Route path="/blog/best-bitcoin-app" element={<BestBitcoinApp />} />
          <Route path="/blog/smart-timing-vs-supercharged" element={<SmartTimingVsSupercharged />} />
          <Route path="/blog/smart-dca-vs-regular-dca" element={<SmartDcaVsDcaCaseStudy />} />
          <Route path="/blog/best-bitcoin-apps-2026" element={<BestBitcoinApps2026 />} />
          <Route path="/download" element={<Download />} />
          <Route path="/calculator" element={<Calculator />} />
          <Route path="/kiosk" element={<KioskCalculator />} />
          <Route path="/satstacker" element={<SatStackerShowRedirect />} />
          <Route path="/bitcoin-dca" element={<BitcoinDca />} />
          <Route path="/smart-dca" element={<SmartDca />} />
          <Route path="/articles/self-custody" element={<SelfCustodyArticle />} />
          <Route path="/articles/dca-vs-lump-sum" element={<DcaVsLumpSum />} />
          <Route
            path="/smart-timing-results"
            element={<SmartTimingResults />}
          />
          <Route
            path="/strike-bitcoin-dca"
            element={<StrikeBitcoinDca />}
          />
          <Route
            path="/product"
            element={<Product />}
          />
        </Route>

        {/* Partner read-only routes — no Layout/sidebar */}
        <Route
          path="/partner"
          element={
            !authReady ? (
              <SplashScreen />
            ) : token ? (
              <PartnerDashboard userInfo={userInfo} />
            ) : (
              <Navigate
                to="/login?redirect=/partner"
                replace
              />
            )
          }
        />

        <Route
          path="/partner-claim"
          element={
            <PartnerClaim
              setToken={setToken}
              fetchUserInfo={fetchUserInfo}
            />
          }
        />
        <Route path="/partner-start" element={<PartnerStart />} />

        {/* App shell routes */}
        <Route element={
          <Layout 
            userInfo={userInfo}
            onLogout={async () => {
              if (Capacitor.isNativePlatform()) {
                try {
                  await Purchases.logOut();
                } catch (error) {
                  console.warn("[RC] logOut failed", error);
                }

                window.__RC_LOGGED__ = undefined;
                window.__USER_ID__ = undefined;
                rcLoggedFor.current = null;
              }

              await logoutSession();

              setToken("");
              setUserInfo(null);
              setFetchAttempted(false);
              navigate("/login");
            }}
          />
        }>
          <Route
            path="/dashboard"
            element={
              !authReady
                ? <SplashScreen />
                : <Dashboard
                    token={token}
                    userInfo={userInfo}
                  />
            }
          />
          <Route path="/withdrawals" element={<Withdrawals token={token} userInfo={userInfo} />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/faq" element={<Faq />} />
          <Route
            path="/verify-email"
            element={
              <VerifyEmail
                setToken={setToken}
                fetchUserInfo={fetchUserInfo}
              />
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword  />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/delete-account-info" element={<DeleteAccountInfo />} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/upgrade" element={<Upgrade />} />
          <Route path="/review" element={<Review />} />
          <Route path="/share" element={<SharePage userInfo={userInfo} />} />
          <Route path="/e/review" element={<Navigate to="/review" replace />} />
          <Route path="/e/upgrade" element={<Navigate to="/upgrade" replace />} />
          <Route path="/e/share" element={<Navigate to="/share" replace />} />
          <Route path="/e/trades" element={<Navigate to="/trades" replace />} />
          <Route path="/e/partner-claim" element={<PartnerClaimEmailRedirect />} />

          {/* 🔐 Protected Routes */}
          <Route
            path="/plans"
            element={!authReady ? (
              <SplashScreen />
            ) : token ? (
              <BuyPlans
                token={token}
                userInfo={userInfo}
                setToken={setToken}
                buyPlans={buyPlans}
                fetchBuyPlans={fetchBuyPlans}
                fetchUserInfo={fetchUserInfo}
              />
            ) : (
              <Navigate
                to={`/login?redirect=${encodeURIComponent(
                  `/plans${location.search}`
                )}`}
                replace
              />
            )}
          />

          <Route
            path="/settings"
            element={
              !authReady ? (
                <SplashScreen />
              ) : token ? (
                <Settings
                  userInfo={userInfo}
                  buyPlans={buyPlans}
                  fetchBuyPlans={fetchBuyPlans}
                />
              ) : (
                <Navigate
                  to="/login?redirect=/settings"
                  replace
                />
              )
            }
          />

          <Route
            path="/change-password"
            element={
              !authReady ? (
                <SplashScreen />
              ) : token ? (
                <ChangePassword />
              ) : (
                <Navigate
                  to="/login?redirect=/change-password"
                  replace
                />
              )
            }
          />

          <Route
            path="/trades"
            element={!authReady ? (
              <SplashScreen />
            ) : token ? (
              <Trades trades={trades} userInfo={userInfo} token={token} />
            ) : (
              <Navigate to="/login?redirect=/trades" replace />
            )}
          />
        </Route>

        {/* Unauthenticated only */}
        <Route
          path="/login"
          element={
            !authReady ? (
              <SplashScreen />
            ) : token ? (
              userInfo ? (
                <Navigate
                  to="/dashboard"
                  replace
                />
              ) : (
                <SplashScreen />
              )
            ) : (
              <Login
                setToken={setToken}
                setUserInfo={setUserInfo}
                fetchUserInfo={fetchUserInfo}
              />
            )
          }
        />
        <Route
          path="/signup"
          element={
            <Signup
              setToken={setToken}
              fetchUserInfo={fetchUserInfo}
              onSuccess={() => {
                navigate("/dashboard", { replace: true });
              }}
            />
          }
        />
        <Route path="/address" element={<AddressForm />} />
        <Route
          path="*"
          element={
            Capacitor.isNativePlatform()
              ? <Navigate to="/" replace />
              : <NotFoundPage />
          }
        />

      </Routes>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Root component wraps AppContent in <Router>                        */
/* ------------------------------------------------------------------ */
export default AppContent;